import {memo, useEffect, useMemo, useState, useCallback, useRef} from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@common-web/components/ui/sheet';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Label} from '@common-web/components/ui/label';
import {ExtractedFile, FileForUpload, PivotConfig, PivotColumn} from '@client/pages/payroll/types';
import {
  Check,
  X,
  Eye,
  TableProperties,
  Info,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Search,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Input} from '@common-web/components/ui/input';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {PayrollAnchorField} from '@api/lib/payrollProcessing/types';
import {trpc} from '@common-web/lib/trpc';
import {guessFileType} from '@client/pages/payroll/utils/fileUtils';
import {fileToBase64} from '@common-web/lib/fileToBase64';

type DeductionPivotModalProps = {
  open: boolean;
  onClose: () => void;
  extractedFiles: ExtractedFile[];
  anchorFields?: PayrollAnchorField[];
  existingPivotConfig?: PivotConfig;
  onConfirm: (config: PivotConfig | null, generatedColumns: PivotColumn[]) => void;
  files: FileForUpload[];
  agentId: string;
};

export const DeductionPivotModal = memo(
  ({
    open,
    onClose,
    extractedFiles,
    anchorFields,
    existingPivotConfig,
    onConfirm,
    files,
    agentId,
  }: DeductionPivotModalProps) => {
    const [selectedFileId, setSelectedFileId] = useState<string>('');
    const [keyColumnIndex, setKeyColumnIndex] = useState<number | null>(null);
    const [valueColumnIndex, setValueColumnIndex] = useState<number | null>(null);
    const [anchorColumnIndex, setAnchorColumnIndex] = useState<number | null>(null);
    const [aggregation, setAggregation] = useState<PivotConfig['aggregation']>('SUM');
    const [labelPrefix, setLabelPrefix] = useState<string>('');
    const [showPreview, setShowPreview] = useState(true);
    const [keyColumnSearch, setKeyColumnSearch] = useState('');
    const [valueColumnSearch, setValueColumnSearch] = useState('');
    const [anchorColumnSearch, setAnchorColumnSearch] = useState('');
    const [allUniqueKeyValues, setAllUniqueKeyValues] = useState<string[]>([]);
    const [isLoadingKeyValues, setIsLoadingKeyValues] = useState(false);
    const [keyValuesError, setKeyValuesError] = useState<string | null>(null);

    const extractPivotKeyValues = trpc.group.payrollProcessing.extractPivotKeyValues.useMutation();
    const wasOpenRef = useRef(false);

    useEffect(() => {
      const justOpened = open && !wasOpenRef.current;
      wasOpenRef.current = open;

      if (!justOpened) return;

      setKeyColumnSearch('');
      setValueColumnSearch('');
      setAnchorColumnSearch('');
      setAllUniqueKeyValues([]);
      setKeyValuesError(null);

      if (existingPivotConfig) {
        setSelectedFileId(existingPivotConfig.fileId);
        setKeyColumnIndex(existingPivotConfig.keyColumn.index);
        setValueColumnIndex(existingPivotConfig.valueColumn.index);
        setAnchorColumnIndex(existingPivotConfig.anchorColumn.index);
        setAggregation(existingPivotConfig.aggregation);
        setLabelPrefix(existingPivotConfig.labelPrefix || '');
      } else {
        const filesWithData = extractedFiles.filter((f) => f.columns.length > 0);
        if (filesWithData.length > 0) {
          setSelectedFileId(filesWithData[0].file.fileId);
        }
        setKeyColumnIndex(null);
        setValueColumnIndex(null);
        setAnchorColumnIndex(null);
        setAggregation('SUM');
        setLabelPrefix('');
      }
    }, [open, existingPivotConfig, extractedFiles]);

    const selectedFile = useMemo(() => {
      return extractedFiles.find((f) => f.file.fileId === selectedFileId);
    }, [extractedFiles, selectedFileId]);

    const columns = useMemo(() => {
      return selectedFile?.columns || [];
    }, [selectedFile]);

    const filteredKeyColumns = useMemo(() => {
      const sortedColumns = [...columns].sort((a, b) => a.name.localeCompare(b.name));
      if (!keyColumnSearch.trim()) return sortedColumns;
      const searchLower = keyColumnSearch.replace(/\s/g, '').toLowerCase();
      return sortedColumns.filter((c) =>
        c.name.replace(/\s/g, '').toLowerCase().includes(searchLower),
      );
    }, [columns, keyColumnSearch]);

    const filteredValueColumns = useMemo(() => {
      const sortedColumns = [...columns].sort((a, b) => a.name.localeCompare(b.name));
      if (!valueColumnSearch.trim()) return sortedColumns;
      const searchLower = valueColumnSearch.replace(/\s/g, '').toLowerCase();
      return sortedColumns.filter((c) =>
        c.name.replace(/\s/g, '').toLowerCase().includes(searchLower),
      );
    }, [columns, valueColumnSearch]);

    const filteredAnchorColumns = useMemo(() => {
      const sortedColumns = [...columns].sort((a, b) => a.name.localeCompare(b.name));
      if (!anchorColumnSearch.trim()) return sortedColumns;
      const searchLower = anchorColumnSearch.replace(/\s/g, '').toLowerCase();
      return sortedColumns.filter((c) =>
        c.name.replace(/\s/g, '').toLowerCase().includes(searchLower),
      );
    }, [columns, anchorColumnSearch]);

    const keyColumn = useMemo(() => {
      if (keyColumnIndex === null) return null;
      return columns.find((c) => c.index === keyColumnIndex) || null;
    }, [columns, keyColumnIndex]);

    const valueColumn = useMemo(() => {
      if (valueColumnIndex === null) return null;
      return columns.find((c) => c.index === valueColumnIndex) || null;
    }, [columns, valueColumnIndex]);

    const anchorColumn = useMemo(() => {
      if (anchorColumnIndex === null) return null;
      return columns.find((c) => c.index === anchorColumnIndex) || null;
    }, [columns, anchorColumnIndex]);

    // Get suggested anchor column from file linking config (if available)
    const suggestedAnchorColumn = useMemo(() => {
      if (!selectedFileId || !anchorFields?.length) return null;

      for (const anchor of anchorFields) {
        if (anchor.type === 'RAW_COLUMNS') {
          const col = anchor.columns.find((c) => c.fileId === selectedFileId);
          if (col) {
            return {name: col.name, index: col.index};
          }
        }
      }
      return null;
    }, [selectedFileId, anchorFields]);

    useEffect(() => {
      if (!keyColumn || !selectedFileId || !open) {
        setAllUniqueKeyValues([]);
        setKeyValuesError(null);
        return;
      }

      const fetchKeyValues = async () => {
        setIsLoadingKeyValues(true);
        setKeyValuesError(null);
        try {
          const selectedFileData = files.find((f) => f.fileId === selectedFileId);
          if (!selectedFileData) {
            setKeyValuesError('Could not find the selected file.');
            setAllUniqueKeyValues([]);
            return;
          }

          const result = await extractPivotKeyValues.mutateAsync({
            agentId,
            files: [
              {
                fileId: selectedFileId,
                fileName: selectedFileData.file.name,
                fileContent: await fileToBase64(selectedFileData.file),
                fileType: guessFileType(selectedFileData.file.name),
                hasHeaders: selectedFileData.hasHeaders,
                delimiter: selectedFileData.delimiter,
              },
            ],
            fileId: selectedFileId,
            keyColumnName: keyColumn.name,
          });

          setAllUniqueKeyValues(result.values);
        } catch (error) {
          console.error('Failed to fetch pivot key values:', error);
          setKeyValuesError('Failed to load deduction codes from file. Please try again.');
          setAllUniqueKeyValues([]);
        } finally {
          setIsLoadingKeyValues(false);
        }
      };

      fetchKeyValues();
    }, [keyColumn, selectedFileId, files, agentId, open]);

    const previewPivotColumns = useMemo(() => {
      if (!keyColumn || !valueColumn || !selectedFile || allUniqueKeyValues.length === 0) return [];

      const keyValues = new Set<string>(
        allUniqueKeyValues.map((v) => String(v).trim()).filter(Boolean),
      );

      const samplesByKey: Record<string, string[]> = {};
      if (valueColumn.samples && keyColumn.samples) {
        for (let i = 0; i < Math.min(keyColumn.samples.length, valueColumn.samples.length); i++) {
          const key = String(keyColumn.samples[i] || '').trim();
          const value = String(valueColumn.samples[i] || '').trim();
          if (key) {
            if (!samplesByKey[key]) {
              samplesByKey[key] = [];
            }
            samplesByKey[key].push(value);
          }
        }
      }

      const previewCols: PivotColumn[] = Array.from(keyValues).map((keyValue, idx) => ({
        name: labelPrefix ? `${labelPrefix}${keyValue}` : keyValue,
        index: -1000 - idx,
        samples: samplesByKey[keyValue] || [],
        pivotKeyValue: keyValue,
        isPivotColumn: true as const,
        fileId: selectedFileId,
      }));

      return previewCols.sort((a, b) => a.name.localeCompare(b.name));
    }, [keyColumn, valueColumn, selectedFile, selectedFileId, labelPrefix, allUniqueKeyValues]);

    const canSave = useMemo(() => {
      return (
        selectedFileId &&
        keyColumn &&
        valueColumn &&
        anchorColumn &&
        allUniqueKeyValues.length > 0 &&
        !keyValuesError &&
        !isLoadingKeyValues
      );
    }, [
      selectedFileId,
      keyColumn,
      valueColumn,
      anchorColumn,
      allUniqueKeyValues,
      keyValuesError,
      isLoadingKeyValues,
    ]);

    const handleSave = useCallback(() => {
      if (!canSave || !keyColumn || !valueColumn || !anchorColumn) return;

      const config: PivotConfig = {
        fileId: selectedFileId,
        keyColumn: {name: keyColumn.name, index: keyColumn.index},
        valueColumn: {name: valueColumn.name, index: valueColumn.index},
        anchorColumn: {name: anchorColumn.name, index: anchorColumn.index},
        aggregation,
        labelPrefix: labelPrefix || undefined,
      };

      onConfirm(config, previewPivotColumns);
      onClose();
    }, [
      canSave,
      selectedFileId,
      keyColumn,
      valueColumn,
      anchorColumn,
      aggregation,
      labelPrefix,
      previewPivotColumns,
      onConfirm,
      onClose,
    ]);

    const handleRemove = useCallback(() => {
      onConfirm(null, []);
      onClose();
    }, [onConfirm, onClose]);

    return (
      <Sheet
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) onClose();
        }}
      >
        <SheetContent className='w-full sm:max-w-2xl lg:max-w-4xl overflow-hidden flex flex-col'>
          <SheetHeader className='shrink-0'>
            <SheetTitle className='flex items-center gap-2 text-internal-brand-navy'>
              <TableProperties className='h-5 w-5 text-internal-brand-blue' />
              Configure Deduction Code Pivot
            </SheetTitle>
            <SheetDescription className='text-sm text-internal-brand-gray-dark'>
              Transform rows with deduction codes into separate columns. For example, convert
              repeated rows with "Deduction Code" and "Deduction Amount" into individual columns
              like "401K", "Dental", etc.
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className='flex-1 mt-6'>
            <div className='flex-1 flex flex-col gap-6 min-h-0'>
              <Alert className='border-internal-brand-blue/30 bg-internal-brand-blue/5'>
                <Info className='h-4 w-4 text-internal-brand-blue' />
                <AlertDescription className='text-sm text-internal-brand-navy'>
                  <strong>How it works:</strong> Select a file with multiple rows per employee (like
                  a deductions file). Choose the column containing the deduction codes/names and the
                  column with amounts. We'll automatically create a new column for each unique code
                  and sum the amounts.
                </AlertDescription>
              </Alert>
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='py-3 bg-gray-50 border-b border-gray-200'>
                  <CardTitle className='text-sm font-semibold text-internal-brand-navy'>
                    1. Select Source File
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-4'>
                  <Select
                    value={selectedFileId}
                    onValueChange={(val) => {
                      setSelectedFileId(val);
                      setKeyColumnIndex(null);
                      setValueColumnIndex(null);
                      setAnchorColumnIndex(null);
                      setKeyColumnSearch('');
                      setValueColumnSearch('');
                      setAnchorColumnSearch('');
                    }}
                  >
                    <SelectTrigger className='h-10'>
                      <SelectValue placeholder='Choose file with deduction rows...' />
                    </SelectTrigger>
                    <SelectContent>
                      {extractedFiles.map((f) => (
                        <SelectItem key={f.file.fileId} value={f.file.fileId}>
                          <div className='flex items-center gap-2'>
                            <FileIdBadge fileId={f.file.fileId} />
                            <span>{f.file.fileName}</span>
                            <Badge variant='outline' className='text-xs ml-2'>
                              {f.columns.length} columns
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
              {!!selectedFile && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardHeader className='py-3 bg-gray-50 border-b border-gray-200'>
                    <CardTitle className='text-sm font-semibold text-internal-brand-navy'>
                      2. Select Key and Value Columns
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='p-4 space-y-4'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label className='text-sm font-medium text-internal-brand-navy'>
                          Code/Key Column
                          <span className='text-xs text-gray-500 font-normal ml-1'>
                            (e.g., "Deduction Code")
                          </span>
                        </Label>
                        <Select
                          value={keyColumnIndex?.toString() || ''}
                          onValueChange={(val) => {
                            setKeyColumnIndex(parseInt(val, 10));
                            setKeyColumnSearch('');
                          }}
                        >
                          <SelectTrigger className='h-10'>
                            <SelectValue placeholder='Select column with codes...' />
                          </SelectTrigger>
                          <SelectContent className='max-h-[400px]'>
                            <div className='p-2 border-b sticky top-0 bg-white z-10'>
                              <div className='relative'>
                                <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-internal-brand-gray' />
                                <Input
                                  placeholder='Search columns...'
                                  value={keyColumnSearch}
                                  onChange={(e) => setKeyColumnSearch(e.target.value)}
                                  className='pl-8 h-8 border-gray-200 focus:border-internal-brand-blue placeholder:text-gray-400 focus:ring-internal-brand-blue'
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            <div className='max-h-[300px] overflow-y-auto'>
                              {filteredKeyColumns.length === 0 ? (
                                <div className='p-4 text-center text-sm text-internal-brand-gray'>
                                  No columns found matching "{keyColumnSearch}"
                                </div>
                              ) : (
                                filteredKeyColumns.map((col) => (
                                  <SelectItem
                                    key={col.index}
                                    value={col.index.toString()}
                                    disabled={col.index === valueColumnIndex}
                                    className='even:bg-gray-50/50 even:border-y even:border-gray-200'
                                  >
                                    <div className='flex flex-col items-start max-w-[300px] min-h-[40px]'>
                                      <span
                                        className='font-medium text-sm text-gray-700'
                                        title={col.name}
                                      >
                                        {col.name}
                                      </span>
                                      {col.samples?.[0] && (
                                        <span
                                          className='text-xs text-gray-600 truncate max-w-[300px]'
                                          title={col.samples[0]}
                                        >
                                          e.g. {col.samples[0]}
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </div>
                          </SelectContent>
                        </Select>
                        {keyColumn && keyColumn.samples?.length > 0 && (
                          <div className='text-xs text-gray-500 mt-1 flex items-center flex-wrap gap-2'>
                            <span>Sample values: </span>
                            {keyColumn.samples.slice(0, 3).map((s, i) => (
                              <Badge key={i} variant='outline' className='font-mono'>
                                {s}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='space-y-2'>
                        <Label className='text-sm font-medium text-internal-brand-navy'>
                          Amount/Value Column
                          <span className='text-xs text-gray-500 font-normal ml-1'>
                            (e.g., "Deduction Amount")
                          </span>
                        </Label>
                        <Select
                          value={valueColumnIndex?.toString() || ''}
                          onValueChange={(val) => {
                            setValueColumnIndex(parseInt(val, 10));
                            setValueColumnSearch('');
                          }}
                        >
                          <SelectTrigger className='h-10'>
                            <SelectValue placeholder='Select column with amounts...' />
                          </SelectTrigger>
                          <SelectContent className='max-h-[400px]'>
                            <div className='p-2 border-b sticky top-0 bg-white z-10'>
                              <div className='relative'>
                                <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-internal-brand-gray' />
                                <Input
                                  placeholder='Search columns...'
                                  value={valueColumnSearch}
                                  onChange={(e) => setValueColumnSearch(e.target.value)}
                                  className='pl-8 h-8 border-gray-200 focus:border-internal-brand-blue placeholder:text-gray-400 focus:ring-internal-brand-blue'
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            <div className='max-h-[300px] overflow-y-auto'>
                              {filteredValueColumns.length === 0 ? (
                                <div className='p-4 text-center text-sm text-internal-brand-gray'>
                                  No columns found matching "{valueColumnSearch}"
                                </div>
                              ) : (
                                filteredValueColumns.map((col) => (
                                  <SelectItem
                                    key={col.index}
                                    value={col.index.toString()}
                                    disabled={col.index === keyColumnIndex}
                                    className='even:bg-gray-50/50 even:border-y even:border-gray-200'
                                  >
                                    <div className='flex flex-col items-start max-w-[300px] min-h-[40px]'>
                                      <span
                                        className='font-medium text-sm text-gray-700'
                                        title={col.name}
                                      >
                                        {col.name}
                                      </span>
                                      {col.samples?.[0] && (
                                        <span
                                          className='text-xs text-gray-600 truncate max-w-[300px]'
                                          title={col.samples[0]}
                                        >
                                          e.g. {col.samples[0]}
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </div>
                          </SelectContent>
                        </Select>
                        {!!valueColumn && valueColumn.samples?.length > 0 && (
                          <div className='text-xs text-gray-500 mt-1 flex items-center flex-wrap gap-2'>
                            Sample values:{' '}
                            {valueColumn.samples.slice(0, 3).map((s, i) => (
                              <Badge key={i} variant='outline' className='font-mono'>
                                {s}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='space-y-2 pt-4 border-t border-gray-200'>
                      <Label className='text-sm font-medium text-internal-brand-navy'>
                        Group By Column
                        <span className='text-xs text-gray-500 font-normal ml-1'>
                          (e.g., "Employee ID")
                        </span>
                      </Label>
                      <Select
                        value={anchorColumnIndex?.toString() || ''}
                        onValueChange={(val) => {
                          setAnchorColumnIndex(parseInt(val, 10));
                          setAnchorColumnSearch('');
                        }}
                      >
                        <SelectTrigger className='h-10'>
                          <SelectValue placeholder='Select column to group rows by...' />
                        </SelectTrigger>
                        <SelectContent className='max-h-[400px]'>
                          <div className='p-2 border-b sticky top-0 bg-white z-10'>
                            <div className='relative'>
                              <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-internal-brand-gray' />
                              <Input
                                placeholder='Search columns...'
                                value={anchorColumnSearch}
                                onChange={(e) => setAnchorColumnSearch(e.target.value)}
                                className='pl-8 h-8 border-gray-200 focus:border-internal-brand-blue placeholder:text-gray-400 focus:ring-internal-brand-blue'
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className='max-h-[300px] overflow-y-auto'>
                            {filteredAnchorColumns.length === 0 ? (
                              <div className='p-4 text-center text-sm text-internal-brand-gray'>
                                No columns found matching "{anchorColumnSearch}"
                              </div>
                            ) : (
                              filteredAnchorColumns.map((col) => (
                                <SelectItem
                                  key={col.index}
                                  value={col.index.toString()}
                                  disabled={
                                    col.index === keyColumnIndex || col.index === valueColumnIndex
                                  }
                                  className='even:bg-gray-50/50 even:border-y even:border-gray-200'
                                >
                                  <div className='flex flex-col items-start max-w-[300px] min-h-[40px]'>
                                    <span
                                      className='font-medium text-sm text-gray-700'
                                      title={col.name}
                                    >
                                      {col.name}
                                      {suggestedAnchorColumn?.index === col.index && (
                                        <span className='ml-2 text-xs text-emerald font-normal'>
                                          (from File Links)
                                        </span>
                                      )}
                                    </span>
                                    {col.samples?.[0] && (
                                      <span
                                        className='text-xs text-gray-600 truncate max-w-[300px]'
                                        title={col.samples[0]}
                                      >
                                        e.g. {col.samples[0]}
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                      {anchorColumn && anchorColumn.samples?.length > 0 && (
                        <div className='text-xs text-gray-500 mt-1 flex items-center flex-wrap gap-2'>
                          <span>Sample values: </span>
                          {anchorColumn.samples.slice(0, 3).map((s, i) => (
                            <Badge key={i} variant='outline' className='font-mono'>
                              {s}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {suggestedAnchorColumn && anchorColumnIndex === null && (
                        <div className='text-xs text-emerald flex items-center gap-1 mt-1'>
                          <Info className='h-3 w-3' />
                          <span>
                            Suggested from File Links:{' '}
                            <button
                              type='button'
                              className='font-medium underline hover:no-underline'
                              onClick={() => setAnchorColumnIndex(suggestedAnchorColumn.index)}
                            >
                              {suggestedAnchorColumn.name}
                            </button>
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              {!!keyColumn && !!valueColumn && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardHeader className='py-3 bg-gray-50 border-b border-gray-200'>
                    <CardTitle className='text-sm font-semibold text-internal-brand-navy'>
                      3. Options
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='p-4 space-y-4'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label className='text-sm font-medium text-internal-brand-navy'>
                          Aggregation Method
                        </Label>
                        <Select
                          value={aggregation}
                          onValueChange={(val) => setAggregation(val as PivotConfig['aggregation'])}
                        >
                          <SelectTrigger className='h-10'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='SUM'>Sum (add all values)</SelectItem>
                            <SelectItem value='AVG'>Average</SelectItem>
                            <SelectItem value='MAX'>Maximum</SelectItem>
                            <SelectItem value='MIN'>Minimum</SelectItem>
                            <SelectItem value='COUNT'>Count occurrences</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='space-y-2'>
                        <Label className='text-sm font-medium text-internal-brand-navy'>
                          Column Name Prefix
                          <span className='text-xs text-gray-500 font-normal ml-1'>(optional)</span>
                        </Label>
                        <Input
                          value={labelPrefix}
                          onChange={(e) => setLabelPrefix(e.target.value)}
                          placeholder="e.g., 'Deduction: '"
                          className='h-10'
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {isLoadingKeyValues && keyColumn && (
                <Card className='border-blue-200 bg-blue-50'>
                  <CardContent className='p-4 flex items-center gap-3'>
                    <Loader2 className='h-4 w-4 animate-spin text-internal-brand-blue' />
                    <span className='text-sm text-internal-brand-navy'>
                      Loading all unique values from {keyColumn.name}...
                    </span>
                  </CardContent>
                </Card>
              )}
              {!isLoadingKeyValues && keyValuesError && (
                <Card className='border-red-200 bg-red-50'>
                  <CardContent className='p-4 flex items-center gap-3'>
                    <AlertCircle className='h-4 w-4 text-red-600' />
                    <span className='text-sm text-red-700'>{keyValuesError}</span>
                  </CardContent>
                </Card>
              )}
              {!isLoadingKeyValues && !keyValuesError && previewPivotColumns.length > 0 && (
                <Card className='border-emerald/20 bg-emerald/5'>
                  <CardHeader
                    className='py-3 bg-gradient-to-r from-emerald/10 to-emerald/20 border-b border-emerald/20 cursor-pointer'
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <CardTitle className='text-sm font-semibold text-internal-brand-navy flex items-center justify-between'>
                      <div className='flex items-center gap-2'>
                        <Eye className='h-4 w-4 text-emerald' />
                        Preview Generated Columns ({previewPivotColumns.length})
                      </div>
                      {showPreview ? (
                        <ChevronUp className='h-4 w-4 text-gray-500' />
                      ) : (
                        <ChevronDown className='h-4 w-4 text-gray-500' />
                      )}
                    </CardTitle>
                  </CardHeader>
                  {showPreview && (
                    <CardContent className='p-4'>
                      <div className='space-y-2'>
                        <div className='text-sm text-gray-600 mb-3'>
                          These columns will be available for mapping in the column configurator:
                        </div>
                        <div className='flex flex-wrap gap-2'>
                          {previewPivotColumns.slice(0, 20).map((col) => (
                            <Badge
                              key={col.pivotKeyValue}
                              variant='outline'
                              className='bg-white border-emerald/30 text-internal-brand-navy'
                            >
                              <TableProperties className='h-3 w-3 mr-1 text-emerald' />
                              {col.name}
                            </Badge>
                          ))}
                          {previewPivotColumns.length > 20 && (
                            <Badge variant='outline' className='bg-gray-50 text-gray-500'>
                              +{previewPivotColumns.length - 20} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}
              {!!keyColumn && !!valueColumn && (
                <Card className='border-gray-200 shadow-sm bg-gray-50/50'>
                  <CardContent className='p-4'>
                    <div className='flex items-center justify-center gap-4 text-sm'>
                      <div className='flex flex-col items-center'>
                        <div className='text-xs text-gray-500 mb-1'>Input Rows</div>
                        <div className='px-3 py-2 bg-white border border-gray-200 rounded-lg'>
                          <div className='font-mono text-xs'>
                            <div className='flex gap-4 border-b pb-1 mb-1'>
                              <span className='font-semibold'>{keyColumn.name}</span>
                              <span className='font-semibold'>{valueColumn.name}</span>
                            </div>
                            {keyColumn.samples?.slice(0, 3).map((sample, i) => (
                              <div key={i} className='flex gap-4 text-gray-600'>
                                <span>{sample}</span>
                                <span>{valueColumn.samples?.[i] || '—'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <ArrowRight className='h-6 w-6 text-internal-brand-blue shrink-0' />
                      <div className='flex flex-col items-center'>
                        <div className='text-xs text-gray-500 mb-1'>Output Columns</div>
                        <div className='px-3 py-2 bg-white border border-emerald/30 rounded-lg'>
                          <div className='font-mono text-xs'>
                            <div className='flex gap-3 border-b pb-1 mb-1'>
                              {previewPivotColumns.slice(0, 3).map((col) => (
                                <span key={col.pivotKeyValue} className='font-semibold'>
                                  {col.name}
                                </span>
                              ))}
                              {previewPivotColumns.length > 3 && (
                                <span className='text-gray-400'>...</span>
                              )}
                            </div>
                            <div className='flex gap-3 text-gray-600'>
                              {previewPivotColumns.slice(0, 3).map((col) => (
                                <span key={col.pivotKeyValue}>{col.samples[0] || '0'}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
          <div className='shrink-0 border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-2'>
            <div className='text-sm text-gray-600'>
              {previewPivotColumns.length > 0 && (
                <span>
                  <span className='font-medium text-internal-brand-navy'>
                    {previewPivotColumns.length}
                  </span>{' '}
                  column{previewPivotColumns.length !== 1 ? 's' : ''} will be generated
                </span>
              )}
            </div>
            <div className='flex items-center gap-2'>
              {existingPivotConfig && (
                <Button
                  variant='ghost'
                  onClick={handleRemove}
                  className='text-red-600 hover:bg-red-50 hover:text-red-700 font-medium'
                >
                  <X className='h-4 w-4 mr-1' /> Remove Pivot
                </Button>
              )}
              <Button
                variant='outline'
                onClick={onClose}
                className='border-gray-300 text-gray-700 hover:bg-gray-50'
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave}
                className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
              >
                <Check className='h-4 w-4 mr-1' />{' '}
                {existingPivotConfig ? 'Update Pivot' : 'Configure Pivot'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  },
);
